import { FC, useEffect } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';
import { AlignContainer, BaseSpinner } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { VipLevelRewardModel } from '@shared/types';
import { reorder } from '@shared/utils';

import { useGetVipLevelRewardsByLevelIdQuery, useUpdateVipLevelRewardsOrderMutation } from '@services';

import { AppDispatch, getVipLevelRewardsByLevelIdSelector, setVipLevelRewards } from '@store';

import { VipLevelRewardsProps } from './types';
import { VipLevelReward } from './VipLevelReward';

import styles from './VipLevelRewards.module.css';

const { vipLevelRewardsContainer, vipLevelRewardsEmptyContainer } = styles;

export const VipLevelRewards: FC<VipLevelRewardsProps> = ({
  vipLevel,
  isItemsExpanded,
  handleVipLevelRewardEditModalOpen,
  handleVipLevelRewardDeleteModalOpen,
}) => {
  const { id: vipLevelId } = vipLevel;
  const dispatch = useDispatch<AppDispatch>();

  const currentVipLevelRewards = useSelector(getVipLevelRewardsByLevelIdSelector(vipLevelId));

  const [updateVipLevelRewardsOrder] = useUpdateVipLevelRewardsOrderMutation();

  const isVipLevelRewardsEmpty = currentVipLevelRewards.length === 0;

  const { data: vipLevelRewards, isLoading: isRewardsLoading } = useGetVipLevelRewardsByLevelIdQuery(vipLevelId, {
    refetchOnMountOrArgChange: true,
    skip: !isVipLevelRewardsEmpty || !isItemsExpanded,
  });

  useEffect(() => {
    if (vipLevelRewards !== undefined) {
      dispatch(
        setVipLevelRewards({
          vipLevelRewards,
          vipLevelId: vipLevelId,
        })
      );
    }
  }, [vipLevelRewards]);

  const handleEventsOrder = async (reorderedTasks: VipLevelRewardModel[]) => {
    const orders = reorderedTasks.map((item) => {
      const { id, order } = item;

      return { id, order };
    });

    await updateVipLevelRewardsOrder({ orders });
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source } = result;

    if (destination === null) {
      return;
    }

    const { index: destinationIndex } = destination!;
    const { index: sourceIndex } = source;

    if (destinationIndex === sourceIndex) {
      return;
    }

    const reorderedRewards = reorder<VipLevelRewardModel>(currentVipLevelRewards, sourceIndex, destinationIndex);

    dispatch(
      setVipLevelRewards({
        vipLevelRewards: reorderedRewards,
        vipLevelId: vipLevelId,
      })
    );

    await handleEventsOrder(reorderedRewards);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <Box
            className={classNames(vipLevelRewardsContainer, {
              [vipLevelRewardsEmptyContainer]: isVipLevelRewardsEmpty,
            })}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {!isRewardsLoading ? (
              <>
                {!isVipLevelRewardsEmpty ? (
                  <>
                    {currentVipLevelRewards.map((vipLevelReward, index) => (
                      <VipLevelReward
                        key={vipLevelReward.id}
                        index={index}
                        vipLevel={vipLevel}
                        vipLevelReward={vipLevelReward}
                        handleVipLevelRewardEditModalOpen={handleVipLevelRewardEditModalOpen}
                        handleVipLevelRewardDeleteModalOpen={handleVipLevelRewardDeleteModalOpen}
                      />
                    ))}
                  </>
                ) : (
                  <Typography align="center">No level rewards</Typography>
                )}
              </>
            ) : (
              <AlignContainer>
                <BaseSpinner />
              </AlignContainer>
            )}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
